import { atom, getDefaultStore } from 'jotai';
import orderBy from 'lodash/orderBy';
import { FileBlock } from 'talkjs/all';

import { IContact, ObjectListEntry, ParticipantCandidate } from './_types';
import * as chatState from './chat.state';
import * as conversationListState from './conversationList.state';

export type ExtendedFileBlock = FileBlock & {
  messageId: string;
  createdAt: number;
};

export const conversationTabs = [
  'messages',
  'files',
  'participants',
  'objects',
] as const;

export const $activeTab = atom<(typeof conversationTabs)[number]>('messages');

export const $conversationFiles = atom<ExtendedFileBlock[]>([]);

$conversationFiles.onMount = () => {
  let ongoingMessagesSubscription: { unsubscribe: () => void } | undefined;

  const store = getDefaultStore();

  const setup = () => {
    const session = store.get(chatState.$talkSession);
    const selectedConversation = store.get(
      conversationListState.$selectedConversation,
    );

    if (!session || !selectedConversation) {
      return;
    }

    const currentPage = 1;
    const pageSize = 20;

    ongoingMessagesSubscription?.unsubscribe();

    const messagesSubscription = session
      .conversation(selectedConversation.id)
      .subscribeMessages((messages) => {
        if (messages?.length === currentPage * pageSize) {
          messagesSubscription.loadMore(pageSize);
        }

        store.set(
          $conversationFiles,
          orderBy(
            messages?.flatMap((message) =>
              message.content
                .filter((content) => content.type === 'file')
                .map((content) => ({
                  ...content,
                  messageId: message.id,
                  createdAt: message.createdAt,
                })),
            ) ?? [],
            (it) => it.createdAt,
            'desc',
          ),
        );
      });

    ongoingMessagesSubscription = messagesSubscription;
  };

  setup();

  const unsubSession = store.sub(chatState.$talkSession, setup);
  const unsubConversation = store.sub(
    conversationListState.$selectedConversation,
    setup,
  );

  return () => {
    unsubSession();
    unsubConversation();
    ongoingMessagesSubscription?.unsubscribe();
  };
};

export const $deleteFile = atom(null, (get, _set, file: ExtendedFileBlock) => {
  const talkSession = get(chatState.$talkSession);
  const selectedConversation = get(conversationListState.$selectedConversation);

  if (!talkSession || !selectedConversation) {
    return;
  }

  talkSession
    .conversation(selectedConversation.id)
    .message(file.messageId)
    .delete();
});

export const $internalParticipants = atom((get) => {
  const selectedConversation = get(conversationListState.$selectedConversation);
  const userId = get(chatState.$userId);

  return orderBy(
    selectedConversation?.participants?.filter(
      (it) => it.user?.custom.contactType === 'internal',
    ) ?? [],
    [(it) => it.user.id === userId, (it) => it.user.name],
    ['desc', 'asc'],
  ).map((it) => ({
    ...it,
    isSelf: it.user.id === userId,
  }));
});

export const $externalParticipants = atom<IContact[]>((get) => {
  const selectedConversation = get(conversationListState.$selectedConversation);

  return orderBy(
    selectedConversation?.participants?.filter(
      (it) => it.user?.custom.contactType === 'external',
    ) ?? [],
    (it) => it.user.name,
    'asc',
  );
});

export const $deleteConversation = atom(
  null,
  (get, set, conversationId: string) => {
    const talkSession = get(chatState.$talkSession);
    const selectedConversation = get(
      conversationListState.$selectedConversation,
    );

    if (!talkSession) {
      return;
    }

    talkSession.conversation(conversationId).set({
      custom: {
        ...selectedConversation?.custom,
        objectsData: JSON.stringify(
          selectedConversation?.custom.objectsData ?? [],
        ),
        isDeleted: 'true',
      },
    });

    set(conversationListState.$selectedConversation, undefined);
  },
);

export const $updateConversationSubject = atom(
  null,
  (get, _set, subject: string) => {
    const selectedConversation = get(
      conversationListState.$selectedConversation,
    );
    const session = get(chatState.$talkSession);

    if (!session || !selectedConversation || !subject.trim()) {
      return;
    }

    session.conversation(selectedConversation.id).set({
      subject: subject.trim(),
    });
  },
);

export const $addParticipant = atom(
  null,
  (get, _set, participant: ParticipantCandidate) => {
    const selectedConversation = get(
      conversationListState.$selectedConversation,
    );
    const session = get(chatState.$talkSession);

    if (!session || !selectedConversation) {
      return;
    }

    session
      .user(participant.id)
      .createIfNotExists({
        name: participant.name,
        email: participant.email,
        phone: participant.phoneNumber,
        custom: {
          contactType: participant.type,
          organizationName: participant.organizationName ?? '',
          applicationUserId: participant.id,
        },
      })
      .then(() => {
        session
          .conversation(selectedConversation.id)
          .participant(participant.id)
          .createIfNotExists({
            access: 'ReadWrite',
          });
      });
  },
);

export const $deleteParticipant = atom(
  null,
  (get, _set, participantId: string) => {
    const selectedConversation = get(
      conversationListState.$selectedConversation,
    );
    const session = get(chatState.$talkSession);

    if (!session || !selectedConversation) {
      return;
    }

    session
      .conversation(selectedConversation.id)
      .participant(participantId)
      .delete();
  },
);

export const $addObject = atom(null, (get, _set, object: ObjectListEntry) => {
  const selectedConversation = get(conversationListState.$selectedConversation);
  const session = get(chatState.$talkSession);

  if (!session || !selectedConversation) {
    return;
  }

  session.conversation(selectedConversation.id).set({
    custom: {
      ...selectedConversation.custom,
      objectsData: JSON.stringify([
        ...selectedConversation.custom.objectsData,
        object,
      ]),
    },
  });
});

export const $deleteObject = atom(null, (get, _set, objectId: string) => {
  const selectedConversation = get(conversationListState.$selectedConversation);
  const session = get(chatState.$talkSession);

  if (!session || !selectedConversation) {
    return;
  }

  session.conversation(selectedConversation.id).set({
    custom: {
      ...selectedConversation.custom,
      objectsData: JSON.stringify(
        selectedConversation.custom.objectsData.filter(
          (it) => it.id !== objectId,
        ),
      ),
    },
  });
});
